import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { AdsConfig, adImageUrl, adsAllowed, fetchAdsConfig } from '@/lib/adsConfig';
import { useStoreSettings, buildUpiLink } from '@/hooks/useStoreSettings';
/** Full-page image ad set by the admin; shows on every page open. */
export function AdsController() {
  const { pathname } = useLocation();
  const [cfg, setCfg] = useState<AdsConfig | null>(null);
  const [img, setImg] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const { data: store } = useStoreSettings();

  useEffect(() => {
    const refresh = () => fetchAdsConfig().then(setCfg).catch(() => {});
    refresh();
    const t = setInterval(refresh, 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (cfg?.imagePath) adImageUrl(cfg.imagePath).then(setImg); else setImg(null);
  }, [cfg?.imagePath]);

  // Show once per page open: remember the pathname already shown, reset on navigation.
  const [shownFor, setShownFor] = useState<string | null>(null);
  useEffect(() => { setShownFor(null); }, [pathname]);

  useEffect(() => {
    if (!cfg || !img) return;
    if (!adsAllowed(cfg, pathname)) { setOpen(false); return; }
    if (shownFor === pathname) return;
    setShownFor(pathname);
    setOpen(true);
  }, [cfg, img, pathname, shownFor]);

  if (!open || !img) return null;

  const upiId = store?.upi_id || '';
  const amount = Number(cfg?.payAmount) || 0;
  const payLink = upiId && amount > 0
    ? buildUpiLink({ upiId, payee: store?.upi_payee_name || '', amount, note: 'ISC Subscription' })
    : '';
  const openPay = () => {
    setOpen(false);
    // Try PhonePe directly; if it isn't installed, fall back to any UPI app.
    window.location.href = payLink.replace('upi://pay', 'phonepe://pay');
    setTimeout(() => { if (!document.hidden) window.location.href = payLink; }, 1500);
  };

  const image = <img src={img} alt="Advertisement" className="max-h-[80vh] max-w-full rounded-lg object-contain shadow-2xl" />;

  const phone = (cfg?.phone || '').replace(/[^0-9+]/g, '');

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/90 backdrop-blur-sm p-4">
      <button
        type="button"
        aria-label="Close ad"
        onClick={() => setOpen(false)}
        className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-card border border-border text-foreground shadow disabled:opacity-40"
      >
        <X className="h-5 w-5" />
      </button>
      <span className="absolute top-5 left-4 rounded bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">Ad</span>
      <div className="flex flex-col items-center gap-3">
        {payLink
          ? <button type="button" onClick={openPay}>{image}</button>
          : cfg?.link
          ? <a href={cfg.link} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>{image}</a>
          : image}
        {phone && (
          <a
            href={`tel:${phone}`}
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-lg"
          >
            📞 Contact us
          </a>
        )}
      </div>
    </div>
  );
}
